import { t } from '@lingui/core/macro';
import type { ContentTranslationUiStatus } from '@duke-hq/shared-constants/contentTranslationStatus';

// One source of truth for rolling per-language statuses into an aggregate state,
// label, and colour, so every admin surface reads the same state the same way.
// Attention ladder, highest first: failed > stale > in-flight > gaps > partial >
// complete > none.
//
// Two distinct notions of "not all the way there": `partial` means some cells
// aren't translated at all (resource completeness); `gaps` means every cell is
// translated but `count` of them are partly in English (segment coverage). Never
// reuse the bare word "partial" for the segment-level thing.
export type CoverageStatus =
  | { kind: 'failed'; count: number }
  | { kind: 'stale'; count: number }
  | { kind: 'in-progress'; ready: number; total: number }
  | { kind: 'gaps'; count: number; total: number }
  | { kind: 'partial'; ready: number; total: number; withGaps: number }
  | { kind: 'complete'; total: number }
  | { kind: 'none' };

// The five buckets a status falls into. The one place this mapping lives, so
// every surface agrees and a new status value is handled in one spot.
export type StatusBucket = 'ready' | 'inProgress' | 'stale' | 'failed' | 'notTranslated';

export function statusBucket(status: ContentTranslationUiStatus): StatusBucket {
  switch (status) {
    case 'available':
    case 'available-partial':
      return 'ready';
    case 'queued':
    case 'generating':
      return 'inProgress';
    case 'stale':
      return 'stale';
    case 'failed':
      return 'failed';
    case 'not-translated':
    // Cancelled counts as not translated in every aggregate; only the cell-level
    // chip renders it distinctly.
    case 'cancelled':
      return 'notTranslated';
  }
}

export type CoverageCounts = {
  ready: number;
  inProgress: number;
  stale: number;
  failed: number;
  notTranslated: number;
  total: number;
  // Carried alongside the buckets, not a bucket: an available-partial cell counts
  // as ready (it is served) AND as withGaps, so progress maths stay honest in
  // both directions.
  withGaps: number;
};

export function countByBucket(statuses: ContentTranslationUiStatus[]): CoverageCounts {
  const counts: CoverageCounts = {
    ready: 0,
    inProgress: 0,
    stale: 0,
    failed: 0,
    notTranslated: 0,
    total: 0,
    withGaps: 0,
  };
  for (const status of statuses) {
    counts.total += 1;
    counts[statusBucket(status)] += 1;
    if (status === 'available-partial') {
      counts.withGaps += 1;
    }
  }
  return counts;
}

export function aggregateStatuses(statuses: ContentTranslationUiStatus[]): CoverageStatus {
  return aggregateCounts(countByBucket(statuses));
}

// Split from aggregateStatuses so callers that already have counts don't re-walk.
export function aggregateCounts(counts: CoverageCounts): CoverageStatus {
  if (counts.failed > 0) {
    return { kind: 'failed', count: counts.failed };
  }
  if (counts.stale > 0) {
    return { kind: 'stale', count: counts.stale };
  }
  if (counts.inProgress > 0) {
    return { kind: 'in-progress', ready: counts.ready, total: counts.total };
  }
  if (counts.ready === counts.total && counts.total > 0) {
    return counts.withGaps > 0
      ? { kind: 'gaps', count: counts.withGaps, total: counts.total }
      : { kind: 'complete', total: counts.total };
  }
  if (counts.ready > 0) {
    return { kind: 'partial', ready: counts.ready, total: counts.total, withGaps: counts.withGaps };
  }
  return { kind: 'none' };
}

// Blue is reserved for genuinely in-flight work; a resting partial set is green
// (some is done), so it never reads as "generating" when nothing is. Gaps stay
// green too: the content is served, the gap is a quality caveat, not an alarm.
export function coverageColorScheme(status: CoverageStatus): string {
  switch (status.kind) {
    case 'failed':
      return 'red';
    case 'stale':
      return 'orange';
    case 'in-progress':
      return 'blue';
    case 'complete':
    case 'gaps':
    case 'partial':
      return 'green';
    case 'none':
      return 'gray';
  }
}

// Shares the chip/switcher lexicon so the same state always reads the same word.
export function coverageLabel(status: CoverageStatus): string {
  switch (status.kind) {
    case 'failed':
      return t`${status.count} failed`;
    case 'stale':
      return t`Needs review`;
    case 'in-progress':
      return t`Translating ${status.ready}/${status.total}`;
    case 'complete':
      return t`Translated`;
    case 'gaps':
      return t`Partly translated`;
    case 'partial':
      return t`${status.ready}/${status.total} translated`;
    case 'none':
      return t`Not translated`;
  }
}

// -- Polling ----------------------------------------------------------------

// Poll cadence while any cell is PENDING, so surfaces catch up to
// AVAILABLE/FAILED without a manual reload. Shared so every surface polls alike.
export const TRANSLATION_POLL_INTERVAL_MS = 1500;

type StatusRow = {
  status: ContentTranslationUiStatus | 'PENDING' | 'AVAILABLE' | 'FAILED' | 'CANCELLED';
};

// True if any row across a course's units is still generating. Shared by the
// coverage card and the units list, which poll the same listByCourse shape.
export function courseHasPendingTranslation(
  units: readonly { resources: readonly { rows: readonly StatusRow[] }[] }[] | undefined,
): boolean {
  return (
    units?.some((unit) =>
      unit.resources.some((resource) => resource.rows.some((row) => row.status === 'PENDING')),
    ) ?? false
  );
}

// -- Compact ages -------------------------------------------------------------

// The queue dashboard's age column: "45s", "12m", "2h 10m", "3d". djs().fromNow()
// is the verbose form ("2 minutes ago"); operational tables need the dense one.
export function formatCompactAge(ageSeconds: number): string {
  const seconds = Math.max(0, Math.floor(ageSeconds));
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }
  return `${Math.floor(hours / 24)}d`;
}
